/**
 * The studio's two tiers, and every value `user.role` may hold.
 *
 * `ARCHITECTURE.md` §Identities and roles. Until session 35 the studio was one
 * word — `user.role = 'staff'` — and `.docs/00-index.md` §6 recorded the decision
 * as "Staff sub-roles | Not built. One `staff` flag." That decision is reversed
 * here: a studio account is now a `manager`, who has exactly the access the single
 * flag used to carry, or a `staff`, who has the task board and nothing else.
 *
 * ## Why it lives in `packages/shared`
 *
 * The same reason `CLIENT_ROLES` does, and `schemas/clients.ts:23-104` writes the
 * argument out at length: **`apps/web` may not import `apps/api`**, so a role
 * vocabulary stated in the API is a vocabulary the SPA has to hand-copy. Before
 * this file there were five copies of the literal `'staff'` — `modules/auth/auth.ts`,
 * `modules/tasks/queries.ts`, `modules/scheduling/service.ts`, `seed/world/staff.ts`
 * and `apps/web/src/auth/principal.tsx` — each correct on the day it was written and
 * one tier away from disagreeing.
 *
 * `modules/tasks/queries.ts` is the interesting one. It re-declared the constant
 * **deliberately**, with a docblock saying why: importing it from `auth.ts` would
 * construct the whole Better Auth instance in order to compare two words. That cost
 * does not exist here — this file is three constants and two predicates — so the
 * documented exception disappears rather than being preserved.
 *
 * ## Why it is not a permission matrix
 *
 * `ROLE_PERMISSIONS` next door is 4 roles × 5 permissions with genuinely
 * incomparable rows: `billing` and `member` each hold something the other does not,
 * which is what makes a table the right shape. The studio is two tiers with one
 * distinction and `manager ⊃ staff`. **A rank is not a matrix**, and modelling it as
 * one would put an indirection in front of a fact that is one word. The day a third
 * tier exists is the day to reconsider — which is exactly what `ARCHITECTURE.md`'s
 * "not needed yet" was recording, correctly, for two tiers ago.
 *
 * What each tier may *do* is not here either, and that is the design rather than an
 * omission: it is declared per action, as `studioRole` on the action's own
 * `defineAction` (`apps/api/src/actions/define.ts`), so a tool is closed to the
 * restricted tier by **saying nothing** — and a tool added next year is closed
 * without anybody remembering a list.
 */
import { z } from 'zod';
/**
 * The two tiers, most trusted first.
 *
 * `manager` is the studio's full access — every action, every studio page, money,
 * impersonation, agents, MCP — and is precisely what `role = 'staff'` meant before
 * session 35. `staff` is the restricted tier: the task board, and nothing beside it.
 *
 * The restricted tier keeps the **word** `staff` rather than taking a new one, and
 * that is worth stating because it looks like a missed opportunity. A migration that
 * renamed it would have to rewrite every existing studio row, and a row it missed
 * would resolve as a client user — the failure this whole change is most careful to
 * avoid (see `0039_studio_roles.sql`). Keeping the word makes an unmigrated row the
 * *restricted* tier, which is the fail-closed direction.
 */
export const MANAGER_ROLE = 'manager';
/**
 * The restricted tier. Named beside `MANAGER_ROLE` rather than left as a literal
 * because two configurations key off it by name — Better Auth's `admin` plugin
 * statement map (`modules/auth/auth.ts`) and the seed — and a bare `'staff'` in
 * either is a fifth copy growing back.
 */
export const RESTRICTED_STAFF_ROLE = 'staff';
export const STUDIO_ROLES = [MANAGER_ROLE, RESTRICTED_STAFF_ROLE];
/**
 * `user.role` for everybody who is not the studio — every client seat.
 *
 * Better Auth's `defaultRole`, and the value `clients.service` writes for an
 * invitee. Named here because `USER_ROLES` is built from it.
 */
export const CLIENT_USER_ROLE = 'user';
/**
 * Every value `user.role` may hold, and what the CHECK in `0039_studio_roles.sql`
 * is built from.
 *
 * **Derived, not written out**, which is the opposite of the rule
 * `schemas/actions.test.ts` applies to `ACTION_SCOPES` ("written out; deriving it
 * would be the constant agreeing with itself"). The difference is what each list is
 * *for*. `ACTION_SCOPES` is a published vocabulary whose membership is the decision,
 * so a test restates it. This one is the union of two lists that are each already
 * the decision, and its only job is to be the same set the database enforces — so
 * deriving it is what makes the constraint and the union unable to drift, and
 * `studio-roles.test.ts` pins the derivation itself.
 */
export const USER_ROLES = [CLIENT_USER_ROLE, ...STUDIO_ROLES];
/**
 * The tier a new studio account gets.
 *
 * `.docs/00-index.md` §6. Restricted, so that widening an account is a deliberate
 * act and forgetting to narrow one is not a silent grant of every studio capability
 * — which is the failure that produced this session: an account created for a new
 * employee arrived able to approve money, issue invoices and impersonate clients.
 *
 * It has no code path that reads it today, and that is not an oversight. Nothing in
 * this product creates a studio account — `ARCHITECTURE.md` §Identities says staff
 * are provisioned outside it and `/admin/set-role` is disabled — so the constant is
 * what `infra/RUNBOOK.md` §8's `INSERT` quotes, and what a `staff_set_role!` action
 * would default to on the day one is built.
 */
export const DEFAULT_STUDIO_ROLE = RESTRICTED_STAFF_ROLE;
/**
 * True when this `user.role` means "one of us", at either tier.
 *
 * **This is the predicate the five hand-written copies were really asking**, and it
 * is why they could all be written as `role === 'staff'` before today: the two
 * questions had one answer. Every one of those sites is a place where an equality
 * test would now silently narrow "is this person the studio" to "is this person
 * restricted", so each becomes a call to this.
 *
 * An unrecognised value is **not** a studio person. It falls through to the client
 * user branch on both sides of the wire, which is exactly today's behaviour for a
 * typo — `principal.tsx` gives the reason at length and it is the one that matters:
 * the failure that matters is the one that grants too much.
 */
export function isStudioRole(value) {
    return STUDIO_ROLES.some((role) => role === value);
}
/**
 * The tier, or null for anybody who is not the studio.
 *
 * `isStudioRole` narrows a string; this one carries the value out, which is what
 * both principal constructors want — they have already read `user.role` to decide
 * whether the caller is studio at all, and re-reading it in twelve services would be
 * twelve queries and twelve chances to read it differently.
 */
export function studioRoleOf(value) {
    return isStudioRole(value) ? value : null;
}
/**
 * The tier as a tool input and an API body would take it.
 *
 * Nothing takes one today — there is no action that sets a tier, by decision
 * (`.docs/reports/35-report.md`) — and it is declared anyway for the reason every
 * other schema in this folder is: the day `staff_set_role!` is built, the enum it
 * validates against has to be this one rather than a fourth copy of the two words.
 */
export const StudioRoleSchema = z
    .enum(STUDIO_ROLES)
    .describe('Which tier a studio account holds. manager is the whole studio — money, clients, ' +
    'agents, impersonation; staff is the task board and nothing else, and is what a new ' +
    'studio account gets (ARCHITECTURE.md, Identities and roles).');
//# sourceMappingURL=studio-roles.js.map